/**
 * Authentication Context
 *
 * Manages user authentication and tenant access.
 * Supports both simple password-based auth (for demos) and WorkOS SSO (for production).
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

// Tenant configurations with access control
export interface TenantConfig {
  id: string;
  name: string;
  slug: string;
  plan: string;
  color: string;
  initial: string;
  password: string; // Per-tenant password for demo access
}

// Tenant access passwords - in production these would come from env/secrets
// Each company has their own password
const TENANT_CONFIGS: TenantConfig[] = [
  {
    id: "lululemon",
    name: "lululemon",
    slug: "lululemon",
    plan: "DEMO",
    color: "#C41230",
    initial: "L",
    password: "lulu2025demo",
  },
  {
    id: "arcteryx",
    name: "Arc'teryx",
    slug: "arcteryx",
    plan: "DEMO",
    color: "#000000",
    initial: "A",
    password: "arcteryx2025demo",
  },
  {
    id: "nespresso",
    name: "Nespresso",
    slug: "nespresso",
    plan: "DEMO",
    color: "#6B4423",
    initial: "N",
    password: "nespresso2025demo",
  },
  {
    id: "lego",
    name: "LEGO",
    slug: "lego",
    plan: "DEMO",
    color: "#FFD500",
    initial: "L",
    password: "lego2025demo",
  },
  {
    id: "nike",
    name: "Nike",
    slug: "nike",
    plan: "DEMO",
    color: "#111111",
    initial: "N",
    password: "nike2025demo",
  },
  {
    id: "adidas",
    name: "Adidas",
    slug: "adidas",
    plan: "DEMO",
    color: "#000000",
    initial: "A",
    password: "adidas2025demo",
  },
  {
    id: "demobrand",
    name: "Demo Brand",
    slug: "demobrand",
    plan: "DEMO",
    color: "#0066CC",
    initial: "D",
    password: "demobrand2025",
  },
  {
    id: "otherware",
    name: "Otherware",
    slug: "otherware",
    plan: "DEMO",
    color: "#2D2D2D",
    initial: "O",
    password: "otherware2025demo",
  },
];

// Pentatonic admin password - can access all tenants
const ADMIN_PASSWORD = "pentatonic2025";

export interface AuthUser {
  tenantId: string;
  tenantName: string;
  isAdmin: boolean; // Pentatonic admin can switch tenants
  authorizedTenants: string[]; // List of tenant IDs user can access
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  canAccessTenant: (tenantId: string) => boolean;
  getAuthorizedTenants: () => TenantConfig[];
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_STORAGE_KEY = "pic_auth_session";

interface StoredSession {
  user: AuthUser;
  expiresAt: number;
  sessionId?: string; // For API-based sessions
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const session: StoredSession = JSON.parse(stored);
        // Check if session is still valid (24 hour expiry)
        if (session.expiresAt > Date.now()) {
          setUser(session.user);
        } else {
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    // Try API authentication first (when running with wrangler)
    try {
      const response = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const { user: apiUser, sessionId } = result.data;

          const authUser: AuthUser = {
            tenantId: apiUser.tenant?.id || "arcteryx",
            tenantName: apiUser.isAdmin
              ? "Pentatonic Admin"
              : apiUser.tenant?.name || "",
            isAdmin: apiUser.isAdmin,
            authorizedTenants:
              apiUser.tenants?.map((t: { id: string }) => t.id) || [],
          };

          const session: StoredSession = {
            user: authUser,
            expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7 days
            sessionId,
          };

          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
          setUser(authUser);
          return { success: true };
        }
      }
    } catch {
      // API not available, fall back to local auth
      console.log("API auth not available, using local auth");
    }

    // Fallback: Check admin password (for frontend-only dev mode)
    if (password === ADMIN_PASSWORD) {
      const adminUser: AuthUser = {
        tenantId: "arcteryx", // Default to first tenant
        tenantName: "Pentatonic Admin",
        isAdmin: true,
        authorizedTenants: TENANT_CONFIGS.map((t) => t.id),
      };

      const session: StoredSession = {
        user: adminUser,
        expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
      setUser(adminUser);
      return { success: true };
    }

    // Fallback: Check tenant-specific passwords
    const tenant = TENANT_CONFIGS.find((t) => t.password === password);
    if (tenant) {
      const tenantUser: AuthUser = {
        tenantId: tenant.id,
        tenantName: tenant.name,
        isAdmin: false,
        authorizedTenants: [tenant.id],
      };

      const session: StoredSession = {
        user: tenantUser,
        expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
      setUser(tenantUser);
      return { success: true };
    }

    return { success: false, error: "Invalid password" };
  };

  const logout = async () => {
    // Try API logout first
    try {
      await fetch("/api/v1/auth/logout", { method: "POST" });
    } catch {
      // Ignore API errors
    }
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setUser(null);
  };

  const canAccessTenant = (tenantId: string): boolean => {
    if (!user) return false;
    return user.isAdmin || user.authorizedTenants.includes(tenantId);
  };

  const getAuthorizedTenants = (): TenantConfig[] => {
    if (!user) return [];
    if (user.isAdmin) return TENANT_CONFIGS;
    return TENANT_CONFIGS.filter((t) => user.authorizedTenants.includes(t.id));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        canAccessTenant,
        getAuthorizedTenants,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Re-export tenant config type without password for external use
export type Tenant = Omit<TenantConfig, "password">;

export function getTenantById(id: string): Tenant | undefined {
  const config = TENANT_CONFIGS.find((t) => t.id === id);
  if (!config) return undefined;
  const { password, ...tenant } = config;
  return tenant;
}
